import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MagicButton from 'components/MagicButton/MagicButton';
import { useGetUserSocialLinksQuery, useSaveSocialLinksMutation } from 'store/services/profile/profileApi';
import { detectPlatform, PLATFORM_ICON_MAP, PLATFORM_LABEL_MAP } from 'utils/socialPlatformDetect';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import './SocialLinksModal.css';

interface SocialLinksModalProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
}

interface LinkRow {
  url: string;
  error: string;
}

const MAX_LINKS = 5;

function validateUrl(url: string): string {
  if (!url.trim()) return '';
  if (!url.startsWith('https://')) return 'URL must start with https://';
  try {
    new URL(url);
    return '';
  } catch {
    return 'Invalid URL format';
  }
}

export const SocialLinksModal: React.FC<SocialLinksModalProps> = ({
  isOpen,
  onClose,
  username,
}) => {
  const { data: socialLinksData } = useGetUserSocialLinksQuery(username, { skip: !isOpen });
  const [saveSocialLinks, { isLoading: isSaving }] = useSaveSocialLinksMutation();
  const [rows, setRows] = useState<LinkRow[]>([]);

  useEffect(() => {
    if (isOpen && socialLinksData) {
      if (socialLinksData.links.length > 0) {
        setRows(socialLinksData.links.map((l) => ({ url: l.url, error: '' })));
      } else {
        setRows([{ url: '', error: '' }]);
      }
    } else if (isOpen) {
      setRows([{ url: '', error: '' }]);
    }
  }, [isOpen, socialLinksData]);

  if (!isOpen) return null;

  const handleUrlChange = (index: number, value: string) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { url: value, error: validateUrl(value) } : row))
    );
  };

  const handleAddRow = () => {
    if (rows.length < MAX_LINKS) {
      setRows((prev) => [...prev, { url: '', error: '' }]);
    }
  };

  const handleRemoveRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // Validate all rows
    const validated = rows.map((row) => ({ ...row, error: validateUrl(row.url) }));
    setRows(validated);

    // Filter out empty rows and check for errors
    const nonEmpty = validated.filter((r) => r.url.trim());
    if (nonEmpty.some((r) => r.error)) return;

    try {
      await saveSocialLinks({
        links: nonEmpty.map((r, i) => ({ url: r.url.trim(), displayOrder: i + 1 })),
      }).unwrap();
      toast.success('Social links updated!');
      onClose();
    } catch {
      toast.error('Failed to save social links.');
    }
  };

  const content = (
    <div className="social-links-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="social-links-modal">
        <div className="social-links-modal-header">
          <div className="social-links-modal-title">
            <img
              src={AssetUrls.icons.elite.badge}
              alt="Elite"
              className="social-links-modal-elite-icon"
            />
            <h2>Social Links</h2>
          </div>
          <button className="social-links-modal-close" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="social-links-modal-content">
          {rows.map((row, index) => {
            const platform = row.url.trim() ? detectPlatform(row.url) : null;
            const iconTuple = platform ? PLATFORM_ICON_MAP[platform] : null;
            const label = platform ? PLATFORM_LABEL_MAP[platform] : null;

            return (
              <div key={index} className="social-links-modal-row">
                <div className="social-links-modal-icon-slot">
                  {iconTuple ? (
                    <FontAwesomeIcon
                      icon={iconTuple}
                      title={label || undefined}
                      className="social-links-modal-platform-icon"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={['fas', 'link']}
                      className="social-links-modal-platform-icon social-links-modal-platform-icon--empty"
                    />
                  )}
                </div>
                <div className="social-links-modal-input-wrapper">
                  <input
                    type="text"
                    className={`social-links-modal-input${row.error ? ' social-links-modal-input--error' : ''}`}
                    value={row.url}
                    onChange={(e) => handleUrlChange(index, e.target.value)}
                    placeholder="https://..."
                  />
                  {row.error && (
                    <span className="social-links-modal-error">{row.error}</span>
                  )}
                </div>
                <button
                  className="social-links-modal-remove-btn"
                  onClick={() => handleRemoveRow(index)}
                  aria-label="Remove link"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            );
          })}

          {rows.length < MAX_LINKS && (
            <button className="social-links-modal-add-btn" onClick={handleAddRow}>
              + Add Link
            </button>
          )}
        </div>

        <div className="social-links-modal-footer">
          <MagicButton label="Cancel" color="dark-grey" onPress={onClose} />
          <MagicButton label="Save" color="purple" onPress={handleSave} isDisabled={isSaving} />
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default SocialLinksModal;
